/**
 * Font preset tokens — wire the active font preset to `--font-sans`, emitted as
 * `.font-{preset}` class blocks by `FontBlockEmitter`. Font is a fourth
 * appearance axis, fully independent of theme, mode and density: it is an
 * `<html>`-level class, NOT a theme property, so `ThemeScope` never re-scopes it
 * and no theme declaration carries fonts.
 * A preset expands to exactly one token, so — unlike `Density` — there is no
 * `tokenNamesFor()` accessor; `FontBlockEmitter` reads `presets[preset].stack`
 * directly.
 * `family` is `null` and `files` is empty for `system`, the one preset that
 * downloads nothing; every other preset names a face declared in
 * `src/css/fonts.css` and one or more `.woff2` files shipped under
 * `src/css/fonts/` — one variable file for most faces, one static file per
 * weight for a face that has no variable cut (Comic Neue). `Font.test.ts` pins
 * the table against `fonts.css` so a renamed file or family cannot drift silently.
 * `ids` is alphabetical (the repo's constant-array rule) and drives the settings
 * schema options and the `ThemeGuards.isFont` type guard. Display order in the
 * UI is deliberately NOT alphabetical and lives in the renderer, exactly as the
 * mode and density lists already do.
 */

export type FontPreset = 'comic-neue' | 'inter' | 'montserrat' | 'quicksand' | 'roboto' | 'system';

export interface FontValue {
  /** Human label shown in Settings and the Storybook toolbar. */
  label: string;
  /** The `@font-face` family name. `null` for `system` (no webfont). */
  family: string | null;
  /** File names under `src/css/fonts/`, each matching one `@font-face` src. Empty for `system`. */
  files: readonly string[];
  /** Full CSS font-family stack written to `--font-sans`. */
  stack: string;
  /** One-line description rendered under the label in Settings. */
  description: string;
}

interface IFont {
  /** Every preset id, alphabetically. Drives the settings schema options and the type guard. */
  ids: readonly FontPreset[];
  /** The default preset applied when nothing is stored. */
  default: FontPreset;
  presets: Readonly<Record<FontPreset, FontValue>>;
}

const SYSTEM_STACK =
  "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Helvetica Neue', Arial, sans-serif";

const FONT_VALUES: Readonly<Record<FontPreset, FontValue>> = {
  'comic-neue': {
    description: 'Casual and friendly; a cleaned-up take on the comic lettering style.',
    family: 'Comic Neue',
    files: ['comic-neue-regular.woff2', 'comic-neue-bold.woff2'],
    label: 'Comic Neue',
    stack: `'Comic Neue', ${SYSTEM_STACK}`
  },
  inter: {
    description: 'The default. Neutral, high x-height, built for screens.',
    family: 'Inter',
    files: ['inter-var.woff2'],
    label: 'Inter',
    stack: `'Inter', ${SYSTEM_STACK}`
  },
  montserrat: {
    description: 'Geometric and wide, drawn from Buenos Aires street signage.',
    family: 'Montserrat',
    files: ['montserrat-var.woff2'],
    label: 'Montserrat',
    stack: `'Montserrat', ${SYSTEM_STACK}`
  },
  quicksand: {
    description: 'Rounded and light; soft geometric letterforms.',
    family: 'Quicksand',
    files: ['quicksand-var.woff2'],
    label: 'Quicksand',
    stack: `'Quicksand', ${SYSTEM_STACK}`
  },
  roboto: {
    description: "Android's default. Mechanical skeleton with open, friendly curves.",
    family: 'Roboto',
    files: ['roboto-var.woff2'],
    label: 'Roboto',
    stack: `'Roboto', ${SYSTEM_STACK}`
  },
  system: {
    description: "Your operating system's font. Downloads nothing.",
    family: null,
    files: [],
    label: 'System',
    stack: SYSTEM_STACK
  }
};

export const Font: IFont = {
  default: 'inter',
  ids: ['comic-neue', 'inter', 'montserrat', 'quicksand', 'roboto', 'system'],
  presets: FONT_VALUES
};
